import {
  Stack,
  Text,
  Box,
  Select,
  styled,
  IconButton,
} from '@ltvco/refresh-lib/theme';
import { LoadingDots } from '@ltvco/refresh-lib/v1';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  People,
} from '@mui/icons-material';
import { useState } from 'react';

import {
  ATTORNEY_ROLES,
  ATTORNEY_ROLE_DISPLAY_MAP,
} from 'routes/Dashboard/CourtCases/v2/constants';
import { CenterContainer } from 'routes/Dashboard/CourtCases/v2/shared';
import { usePartiesAttorneysData } from 'routes/Dashboard/CourtCases/v2/shared/hooks';
import { Attorney, Party } from 'routes/Dashboard/CourtCases/v2/types';

import { DarkenedBox, FadedText, GrayBorderStack } from './components';

interface MessagePartiesAttorneysProps {
  courtCaseId: string;
}

const StyledSelect = styled(Select)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
  borderRadius: '32px',
  width: '200px',
  height: '34px',
  '& .MuiSelect-icon': {
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.text.secondary,
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: '50px',
  },
}));

const AccordionContainer = ({
  display,
  children,
}: {
  display: string;
  children: React.ReactNode;
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <Stack spacing={2} px={2}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <People />
          <Text fontWeight="bold">{display}</Text>
        </Box>
        {expanded ? (
          <IconButton
            onClick={() => setExpanded(false)}
            aria-label="Collapse parties and attorneys"
            size="small"
          >
            <KeyboardArrowUp />
          </IconButton>
        ) : (
          <IconButton
            onClick={() => setExpanded(true)}
            aria-label="Expand parties and attorneys"
            size="small"
          >
            <KeyboardArrowDown />
          </IconButton>
        )}
      </Box>
      {expanded ? <Stack>{children}</Stack> : null}
    </Stack>
  );
};

export const MessagePartiesAttorneys = ({
  courtCaseId,
}: MessagePartiesAttorneysProps) => {
  const partiesAttorneysResult = usePartiesAttorneysData(courtCaseId);
  const {
    attorneysData,
    partiesData,
    partiesCount,
    isLoading,
    hasError,
    error,
  } = partiesAttorneysResult;

  const [selectedPartyType, setSelectedPartyType] = useState<string>('');

  if (isLoading) {
    return (
      <CenterContainer>
        <LoadingDots count={5} />
      </CenterContainer>
    );
  }

  if (hasError) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to load parties and attorneys data';
    return (
      <CenterContainer>
        <Text variant="body1" color="error">
          Error loading parties and attorneys data: {errorMessage}
        </Text>
      </CenterContainer>
    );
  }

  // Helper function to group and sort parties by their primary party type
  const groupPartiesByType = (parties: Party[]) => {
    const grouped: { [key: string]: Party[] } = {};

    // Group parties by type
    parties.forEach((party) => {
      // Get the primary party type (first one or default to 'Other')
      const primaryPartyType = party.party_types?.[0]?.name || 'Other';
      if (!grouped[primaryPartyType]) {
        grouped[primaryPartyType] = [];
      }
      grouped[primaryPartyType].push(party);
    });

    // Sort parties alphabetically within each group
    Object.keys(grouped).forEach((partyType) => {
      grouped[partyType].sort((a, b) => {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
    });

    return grouped;
  };

  // Helper function to get attorney details for a specific party
  const getAttorneysForParty = (party: Party): Attorney[] => {
    if (!attorneysData.length || !party.attorneys) {
      return [];
    }

    // Get attorney IDs from the party's attorneys array
    const attorneyIds = party.attorneys.map((a) => a.attorney_id);

    // Find matching attorneys from the attorneys data
    const matchingAttorneys = attorneysData.filter((attorney) =>
      attorneyIds.includes(attorney.id)
    );

    // Sort attorneys alphabetically by name
    return matchingAttorneys.sort((a, b) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
  };

  // Helper function to parse attorney address from contact_raw
  const parseAttorneyAddress = (attorney: Attorney): string[] => {
    const contact = attorney.contact_raw || '';
    if (!contact) {
      return [];
    }

    // Split by lines and filter out empty lines
    const lines = contact.split('\n').filter(Boolean);

    // Filter out phone, fax, and email lines to get address components
    const addressLines = lines.filter((line) => {
      const lowerLine = line.toLowerCase();
      return (
        !lowerLine.includes('phone') &&
        !lowerLine.includes('fax') &&
        !lowerLine.includes('@') &&
        !lowerLine.includes('email') &&
        !lowerLine.match(/^\s*\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/)
      ); // Basic phone pattern
    });

    return addressLines;
  };

  // Helper function to get party type extra info
  const getPartyTypeExtraInfo = (party: Party): string => {
    return party.party_types?.[0]?.extra_info || party.extra_info || '';
  };

  // Helper function to get attorney role display texts based on their roles for a specific party
  const getAttorneyRoleDisplays = (
    attorney: Attorney,
    party: Party
  ): string[] => {
    if (!party.attorneys || party.attorneys.length === 0) {
      return [ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]];
    }

    // Find the attorney's roles for this specific party
    const attorneyRoles = party.attorneys
      .filter((a) => a.attorney_id === attorney.id)
      .map((a) => a.role);

    if (attorneyRoles.length === 0) {
      return [ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]];
    }

    // Handle Pro Se role (takes precedence over other roles)
    if (attorneyRoles.includes(ATTORNEY_ROLES.PRO_SE)) {
      return [ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.PRO_SE]];
    }

    // Collect all applicable role display texts
    const roleDisplayTexts: string[] = [];

    // Check for Lead Attorney roles (2 and 4) - add first for priority display
    if (
      attorneyRoles.includes(ATTORNEY_ROLES.LEAD_ATTORNEY) ||
      attorneyRoles.includes(ATTORNEY_ROLES.SPECIAL_ROLE)
    ) {
      roleDisplayTexts.push(
        ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.LEAD_ATTORNEY]
      );
    }

    // Check for Attorney to be noticed role (1)
    if (attorneyRoles.includes(ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED)) {
      roleDisplayTexts.push(
        ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]
      );
    }

    // Return all applicable roles, or default if none found
    return roleDisplayTexts.length > 0
      ? roleDisplayTexts
      : [ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]];
  };

  const partiesByType =
    partiesData.length > 0 ? groupPartiesByType(partiesData) : {};

  // Sort party types alphabetically
  const sortedPartyTypes = Object.keys(partiesByType).sort((a, b) => {
    return a.localeCompare(b);
  });

  const partyTypeOptions = [{ display: 'View: All', value: '' }].concat(
    sortedPartyTypes.map((partyType) => ({
      display: partyType,
      value: partyType,
    }))
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2 }}>
          <Text fontWeight="bold">Parties and Attorneys</Text>
          <Text>({partiesCount})</Text>
        </Box>
        <StyledSelect
          id="partyType"
          value={selectedPartyType}
          options={partyTypeOptions}
          onOptionSelect={(value) => setSelectedPartyType(value)}
        />
      </Box>
      {sortedPartyTypes.length > 0 ? (
        <Stack spacing={2}>
          {sortedPartyTypes.map((partyType) => {
            if (selectedPartyType && selectedPartyType !== partyType) {
              return null;
            }

            const parties = partiesByType[partyType];

            return (
              <AccordionContainer key={partyType} display={partyType}>
                <Stack spacing={2}>
                  {parties.map((party: Party) => {
                    const partyAttorneys = getAttorneysForParty(party);
                    const extraInfo = getPartyTypeExtraInfo(party);

                    return (
                      <DarkenedBox key={party.id}>
                        {/* Left side - Party Name */}
                        <Stack spacing={0.5} my={1}>
                          <Text fontWeight="bold">{party.name}</Text>
                          {extraInfo && (
                            <FadedText fontSize={12}>{extraInfo}</FadedText>
                          )}
                        </Stack>

                        {party.attorneys && party.attorneys.length > 0 ? (
                          <Stack spacing={0.5}>
                            {partyAttorneys.map((attorney: Attorney) => {
                              const addressLines =
                                parseAttorneyAddress(attorney);

                              return (
                                <GrayBorderStack
                                  key={attorney.id}
                                  spacing={1}
                                  sx={{ py: 1 }}
                                >
                                  <FadedText fontSize={12}>
                                    Represented By
                                  </FadedText>
                                  <Text fontWeight="bold">{attorney.name}</Text>

                                  {attorney.phone && (
                                    <FadedText>{attorney.phone}</FadedText>
                                  )}

                                  {attorney.fax && (
                                    <FadedText>Fax: {attorney.fax}</FadedText>
                                  )}

                                  {addressLines.length > 0 && (
                                    <Box sx={{ mb: { xs: 0.25, md: 0.5 } }}>
                                      {addressLines.map(
                                        (line: string, lineIndex: number) => (
                                          <FadedText key={lineIndex}>
                                            {line}
                                          </FadedText>
                                        )
                                      )}
                                    </Box>
                                  )}

                                  <Box>
                                    {getAttorneyRoleDisplays(
                                      attorney,
                                      party
                                    ).map(
                                      (roleText: string, roleIndex: number) => (
                                        <FadedText key={roleIndex}>
                                          {roleText}
                                        </FadedText>
                                      )
                                    )}
                                  </Box>
                                </GrayBorderStack>
                              );
                            })}
                          </Stack>
                        ) : (
                          <GrayBorderStack spacing={0.5} sx={{ py: 1 }}>
                            <FadedText fontSize={12}>Represented By</FadedText>
                            <FadedText fontWeight="bold">
                              No attorney assigned
                            </FadedText>
                          </GrayBorderStack>
                        )}
                      </DarkenedBox>
                    );
                  })}
                </Stack>
              </AccordionContainer>
            );
          })}
        </Stack>
      ) : (
        <CenterContainer>
          <Text variant="body1" color="text.secondary">
            No parties and attorneys data available
          </Text>
        </CenterContainer>
      )}
    </Stack>
  );
};
